import type { CompanyDirectoryEntry } from "../utils/segment-connections"

import { ChevronRight } from "@/components/icons"

import {
  COMPANY_FILL,
  groupCompaniesByDisplayRole,
  relatedCompanies,
} from "../utils/segment-connections"
import { relatedCompanySegmentId } from "../utils/segment-drillin"

/**
 * "Related" lateral-nav row: companies sharing at least one segment with
 * `companyName`, as chips that drill into that company. Shared by the company
 * dossier and the CCP / Competitor Plan views. Grouped as Customer /
 * Competitor / Both — same role resolution and swatch colours as the map
 * bubbles/legend (#3306 #15). Renders nothing without a drill-in handler or
 * when there is nothing related.
 */
export function RelatedCompaniesRow({
  companyName,
  directory = [],
  limit = 8,
  preferSegmentId,
  onOpenCompany,
}: {
  companyName: string
  directory?: CompanyDirectoryEntry[]
  limit?: number
  /** Open related companies in this segment when they are in it too. */
  preferSegmentId?: string | null
  onOpenCompany?: (name: string, segmentId: string) => void
}) {
  const related = onOpenCompany ? relatedCompanies(directory, companyName).slice(0, limit) : []
  if (related.length === 0) return null
  const groups = groupCompaniesByDisplayRole(related)

  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
        Related
      </span>
      {groups.map((group) => (
        <div
          key={group.role}
          aria-label={group.label}
          className="flex flex-wrap items-center gap-1.5"
          role="group"
        >
          <span className="text-muted-foreground inline-flex items-center gap-1 text-[11px] font-medium">
            <span
              className="size-1.5 rounded-full"
              style={{ background: COMPANY_FILL[group.role] }}
            />
            {group.label}
          </span>
          {group.entries.map((entry) => (
            <button
              key={entry.name}
              className="btn-spring border-border bg-card text-foreground hover:bg-accent inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition"
              type="button"
              onClick={() => {
                const segmentId = relatedCompanySegmentId(entry, preferSegmentId)
                if (segmentId) onOpenCompany?.(entry.name, segmentId)
              }}
            >
              {entry.name}
              <ChevronRight className="size-3" />
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}
